import React, { useState, useMemo } from "react";
import AppointmentList from "./List";
import { SimpleReactSelectField } from "../../components/ReactHooksFormFields/ReactSelectField";
import MainPageDialog from "../../components/mainPageDialog";
import AppointmentLocation from "./AppointmentLocation";
import AppointmentProvider from "./AppointmentProvider";
import { useForm } from "react-hook-form";
import {
  useAddAppointmentByLocationMutation,
  useGetAppointmentsQuery,
} from "../../redux/service/appointments_service";
import moment from "moment";
import { toast } from "react-toastify";
import { getUser, hasActionRights } from "../../userStore";
import ProviderList from "./providerList";
import LocationList from "./locationList";
import { VESPER_MEDICAL_CARE_PRACTICE_ID, BELLY_HEALTH_PRACTICE_ID } from "../../constants/practiceIds";
import { getAppointmentSubmitStatus, getAppointmentSuccessMessage } from "./appointmentHelpers";

function Appointments() {
  const [saveAppointmentByLocation, { isLoading }] =
    useAddAppointmentByLocationMutation();
  const {
    data,
    isLoading: isLoadingList,
    refetch,
  } = useGetAppointmentsQuery({}, { refetchOnMountOrArgChange: true });

  const user = getUser();
  const [selectedProvider, setSelectedProvider] = useState({});
  const [selectedLocationForBooking, setSelectedLocationForBooking] = useState(null);
  const [showDialog, setShowDialog] = useState({
    show: false,
    type: "",
  });

  const [successfulModal, setSuccessfulModal] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    date: new Date(),
  });

  const submitAppointmentByLocation = async (data) => {
    const body = {
      added_by: "patient",
      date: moment(data?.date).format("YYYY-MM-DD"),
      location_id: data?.location,
      reason: data?.reason,
      specialty: data?.specialty,
      start_time: "",
      status: getAppointmentSubmitStatus(user?.practice_id),
      pid: user?.pid,
      duration: 15,
    };
    const result = await saveAppointmentByLocation(body);

    if (result.error) {
      console.error("Failed to save appointment", result.error);
    }
    if (result?.data?.code == -1) {
      toast.error(result?.data?.msg);
    }
    if (result.data?.code === 0) {
      toast.success("Appointment saved successfully");
      refetch();
      setShowDialog({
        show: false,
        type: "",
      });
      reset();
      setValue("location", "");
      setValue("reason", "");
      setValue("specialty", "");
      setValue("date", "");
      setSuccessfulModal({ state: true, appointmentData: result?.data?.data });
    } else {
      toast.error(result?.data?.msg);
    }
  };

  const footerList = [
    {
      title: "Cancel",
      type: "cancel",
      action: () =>
        setShowDialog({
          show: false,
          type: "",
        }),
    },
    {
      title: isLoading
        ? "Saving..."
        : showDialog?.type == "provider" || showDialog?.type == "bookByLocation"
        ? "Select"
        : "Proceed",
      type: "save",
      action: () => {
        if (showDialog?.type === "provider") {
          handleSubmit(() => {
            setShowDialog({ show: false, type: "provider" });
          })();
        } else if (showDialog?.type === "bookByLocation") {
          if (selectedLocationForBooking?.id) {
            setShowDialog({ show: false, type: "bookByLocation" });
          }
        } else {
          handleSubmit((data) => {
            submitAppointmentByLocation(data);
          })();
        }
      },
    },
  ];

  const access = hasActionRights("appointments", "add");
  const showAppointmentTime=hasActionRights("appointment_time", "view");
  
  const saveAppointmentHandler = async (data) => {
    const body = {
      added_by: "patient",
      location_id: data?.location,
      comments: data?.reason,
      status: getAppointmentSubmitStatus(user?.practice_id),
      pid: user?.pid,
      duration: 15,
      title: "Office Visit",
      start_time: data.selectTime
        ? `${data?.selectTime?.date} ${data?.selectTime?.time}`
        : "",
      user_id: data?.provider || selectedProvider?.id,
      cat_id: data?.cat_id,
      conf_call: data?.conf_call,
      external_guest: data?.external_guest,
      calender_id: data?.selectTime?.calender_id,
    };

    const result = await saveAppointmentByLocation(body);

    if (result.error) {
      toast.error("Failed to save appointment");
    }
    if (result?.data?.code == -1) {
      toast.error(result?.data?.msg);
    } else if (result.data?.code === 0) {
      toast.success("Appointment saved successfully");
      refetch();
      setSelectedProvider({});
      setShowDialog({
        show: false,
        type: "",
      });
      reset();
      setSuccessfulModal({ state: true, appointmentData: result?.data?.data });
    } else {
      toast.error(result?.data?.msg);
    }
  };

  const goBack = () => {
    setShowDialog({
      show: false,
      type: "",
    });
    setSelectedProvider({});
    setSelectedLocationForBooking(null);
  };
  
  const isVesper = user?.practice_id.toString() === VESPER_MEDICAL_CARE_PRACTICE_ID.toString();
  const isBellyHealth = user?.practice_id.toString() === BELLY_HEALTH_PRACTICE_ID.toString();
  const shouldShowProvider = isVesper || user?.practice_id==1235;

  const appointmentOptions = useMemo(() => {
    const options = [];
    if (!shouldShowProvider) options.push({ value: "provider", label: "Provider" });
    options.push({ value: "location", label: "Location" });
    return options;
  }, [shouldShowProvider]);

  const requestAppointmentHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDialog({ show: true, type: "bookByLocation" });
  };

  return (
    <>
      {successfulModal?.state && (
        <MainPageDialog
          title="Appointment"
          modalSize="md"
          maxHeight={true}
          footerList={[
            {
              title: "Ok",
              type: "save",
              action: () => {
                setSuccessfulModal(false);
              },
            },
          ]}
          onCancelHandler={() => setSuccessfulModal(false)}
          show={successfulModal}>
          <div className="p-4">
            <div className="text-center">
              <p className="text-lg">Appointment saved successfully</p>
            </div>
            <div>
              <div>
                <p className="text-start text-red-700 font-bold mt-2 ">
                  {getAppointmentSuccessMessage(
                    user?.practice_id,
                    successfulModal?.appointmentData
                  )}
                </p>
              </div>
            </div>
          </div>
        </MainPageDialog>
      )}
      {showDialog?.type === "provider" && showDialog.show == false ? (
        <AppointmentProvider
          selectedProvider={selectedProvider}
          goBack={goBack}
          saveAppointmentHandler={saveAppointmentHandler}
          isSaving={isLoading}
        />
      ) : showDialog?.type === "bookByLocation" && showDialog.show == false ? (
        <AppointmentProvider
          mode="bookByLocation"
          selectedLocationForBooking={selectedLocationForBooking}
          goBack={goBack}
          saveAppointmentHandler={saveAppointmentHandler}
          isSaving={isLoading}
        />
      ) : (
        <>
          {showDialog?.show && (
            <MainPageDialog
              title={
                showDialog?.type === "provider"
                  ? "Select Provider"
                  : showDialog?.type === "bookByLocation"
                  ? "Select Location"
                  : "Appointment by Location"
              }
              modalSize="md"
              hideOverFlow={showDialog?.type === "provider" || showDialog?.type === "bookByLocation" ? false : true}
              maxHeight={true}
              footerList={footerList}
              onCancelHandler={() =>
                setShowDialog({
                  show: false,
                  type: "",
                })
              }
              show={showDialog.show}>
              <div className="pb-8 px-4 ">
                {showDialog?.type === "provider" ? (
                  <ProviderList
                    selectedProvider={selectedProvider}
                    setSelectedProvider={setSelectedProvider}
                  />
                ) : showDialog?.type === "bookByLocation" ? (
                  <LocationList
                    selectedLocation={selectedLocationForBooking}
                    setSelectedLocation={setSelectedLocationForBooking}
                  />
                ) : (
                  <AppointmentLocation control={control} errors={errors} />
                )}
              </div>
            </MainPageDialog>
          )}

          <div className="p-6  overflow-x-auto ">
            <div className="flex justify-between">
              <h1 className="text-start font-bold text-3xl">
                Your Appointments
              </h1>
              <div className="flex gap-2">
                {access.toString() === "1" && (
                  <>
                    {isBellyHealth ? 
                      <button 
                        onClick={(e) => requestAppointmentHandler(e)} 
                        className="rounded bg-white px-2 h-8 mt-6 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        title="Request Appointment"
                        aria-label="Request Appointment"
                      >
                        Request Appointment
                      </button>
                      : 
                      <div className="font-bold w-full">
                        <SimpleReactSelectField
                          name="appointment"
                          title="Request Appointment"
                          options={appointmentOptions}
                          onChange={(e) => {
                            setShowDialog({ show: true, type: e?.value });
                          }}
                          placeholder="Please select"
                        />
                      </div>
                    }
                  </>
                )}
                <button
                  onClick={refetch}
                  className="rounded bg-white px-2 h-8 mt-6 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <AppointmentList data={data} isLoadingList={isLoadingList} showAppointmentTime={showAppointmentTime}/>
          </div>
        </>
      )}
    </>
  );
}

export default Appointments;
